import { useCallback, useRef, useState } from 'react';

export type QuickChargeMode = 'charge' | 'refund' | 'service';
export type QuickChargeTender =
  | { kind: 'cash' }
  | { kind: 'pinpad'; terminalId: number; pinpadId: string }
  | { kind: 'manual'; terminalId: number; pinpadId: string }
  | { kind: 'top' };

export type QuickChargePreparedPayment = {
  orderId: number;
  paymentlineId: number;
  tender: QuickChargeTender;
};

type QuickChargeStep = 'closed' | 'tender' | 'credit' | 'amount' | 'refund-code';

type UseQuickChargeParams = {
  openQuickChargeOrder: () => Promise<number | null>;
  applyAmount: (
    orderId: number,
    amount: number,
    mode: QuickChargeMode,
    refundCode?: string,
  ) => Promise<number | null>;
  cancelOrder: (orderId: number) => Promise<void> | void;
  clearAmountPrompt: () => void;
  onPrepared: (payment: QuickChargePreparedPayment) => void;
  onError?: (message: string) => void;
};

type RefundChallengeError = Error & { ownerEmail?: string; deliveryTargetLabel?: string };

// Architectural summary: Drives the direct quick-charge wizard and hands prepared paymentlines to existing tender handlers.
export function useQuickCharge({
  openQuickChargeOrder,
  applyAmount,
  cancelOrder,
  clearAmountPrompt,
  onPrepared,
  onError,
}: UseQuickChargeParams) {
  const [step, setStep] = useState<QuickChargeStep>('closed');
  const [tender, setTender] = useState<QuickChargeTender | null>(null);
  const [mode, setMode] = useState<QuickChargeMode>('charge');
  const [refundOwnerEmail, setRefundOwnerEmail] = useState<string | null>(null);
  const [refundDeliveryTargetLabel, setRefundDeliveryTargetLabel] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const orderIdRef = useRef<number | null>(null);
  const amountRef = useRef(0);

  const reset = useCallback(() => {
    setStep('closed');
    setTender(null);
    setMode('charge');
    setRefundOwnerEmail(null);
    setRefundDeliveryTargetLabel(null);
    orderIdRef.current = null;
    amountRef.current = 0;
  }, []);

  const start = useCallback(() => {
    reset();
    setStep('tender');
  }, [reset]);

  const selectCash = useCallback(() => {
    setTender({ kind: 'cash' });
    setStep('amount');
  }, []);

  const selectCredit = useCallback(() => setStep('credit'), []);

  const selectCreditMethod = useCallback((nextTender: Exclude<QuickChargeTender, { kind: 'cash' }>) => {
    setTender(nextTender);
    setStep('amount');
  }, []);

  const back = useCallback(() => {
    setStep((current) => current === 'credit' ? 'tender' : 'tender');
    setTender(null);
    setRefundOwnerEmail(null);
    setRefundDeliveryTargetLabel(null);
  }, []);

  const cancel = useCallback(async () => {
    if (submitLockRef.current) return;
    const orderId = orderIdRef.current;
    clearAmountPrompt();
    reset();
    if (orderId != null) {
      try {
        await cancelOrder(orderId);
      } catch (error: unknown) {
        onError?.(error instanceof Error ? error.message : 'Failed canceling quick charge');
      }
    }
  }, [cancelOrder, clearAmountPrompt, onError, reset]);

  const prepare = useCallback(async (amount: number, refundCode?: string): Promise<boolean> => {
    if (!tender || submitLockRef.current) return false;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      let orderId = orderIdRef.current;
      if (orderId == null) {
        orderId = await openQuickChargeOrder();
        if (orderId == null) return false;
        orderIdRef.current = orderId;
        clearAmountPrompt();
      }
      amountRef.current = amount;
      const paymentlineId = await applyAmount(orderId, amount, mode, refundCode);
      if (paymentlineId == null) return false;
      clearAmountPrompt();
      onPrepared({ orderId, paymentlineId, tender });
      reset();
      return true;
    } catch (error: unknown) {
      const challenge = error as RefundChallengeError;
      if (mode === 'refund' && challenge.message === 'REFUND_2FA_REQUIRED') {
        setRefundOwnerEmail(challenge.ownerEmail ?? null);
        setRefundDeliveryTargetLabel(challenge.deliveryTargetLabel ?? null);
        setStep('refund-code');
      } else {
        onError?.(error instanceof Error ? error.message : 'Failed starting quick charge');
      }
      return false;
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  }, [applyAmount, clearAmountPrompt, mode, onError, onPrepared, openQuickChargeOrder, reset, tender]);

  const confirmAmount = useCallback(async (amount: number): Promise<boolean> => {
    return prepare(amount);
  }, [prepare]);

  const confirmRefundCode = useCallback(async (code: string): Promise<boolean> => {
    if (!/^\d{6}$/.test(code)) return false;
    return prepare(amountRef.current, code);
  }, [prepare]);

  return {
    step,
    tender,
    mode,
    setMode,
    refundOwnerEmail,
    refundDeliveryTargetLabel,
    isSubmitting,
    start,
    selectCash,
    selectCredit,
    selectCreditMethod,
    back,
    cancel,
    confirmAmount,
    confirmRefundCode,
  };
}
